<template>
  <div class="performance-widget-container">
    <widget-header>{{ config.name || 'Possibility of Passing' }}</widget-header>
    <div class="performance-pop-widget">
      <loading-bar v-if="fetching" />
      <template v-else>
        <div class="widget-content">
          <pop-bar-chart
            :level="activeLevel"
            :next-update-progress="readinessState.nextUpdateProgress || 0"
            :next-update-total="readinessState.nextUpdateTotal || 0"
            :class="{ 'chart-bg-only': !hasReadinessLevel }"
          />
          <div
            v-if="fetchError"
            class="pre-overlay"
            data-test="error-overlay"
          >
            <div class="icon-circle">
              <icon name="bar-chart" variant="155EEF" />
            </div>
            <p class="overlay-description">
              Unable to load your progress. Please try again later.
            </p>
          </div>
          <div
            v-else-if="!hasReadinessLevel"
            class="pre-overlay"
            data-test="pre-overlay"
          >
            <div class="icon-circle">
              <icon name="bar-chart" variant="155EEF" class="bar-chart-icon" />
            </div>
            <p class="overlay-description">
              Keep answering questions to help us calculate your likelihood of passing.
            </p>
            <div class="progress-row">
              <div class="progress-track">
                <div
                  class="progress-fill"
                  data-test="progress-fill"
                  :style="{ width: progressPercent + '%' }"
                />
              </div>
              <span class="progress-text" data-test="progress-text">{{ progressText }}</span>
            </div>
          </div>
        </div>
        <div class="widget-actions">
          <button
            class="info-button"
            data-test="info-button"
            aria-label="Possibility of Passing info"
            @click="openInfoModal"
          >
            <icon
              name="help-circle"
              class="help-circle"
            />
          </button>
          <button
            v-if="hasReadinessLevel"
            class="insights-button text-sm font-semibold"
            data-test="insights-button"
            @click="navigateToInsights"
          >
            Insights
          </button>
          <button
            v-else
            class="answer-button"
            data-test="answer-button"
            @click="navigateToQuestions"
          >
            Answer Questions
          </button>
        </div>
        <modal-base
          :is-open="showInfoModal"
          @close="showInfoModal = false"
        >
          <div class="info-modal-content">
            <div
              v-for="faq in faqs"
              :key="faq.id"
              class="faq-item"
            >
              <h4>{{ faq.question }}</h4>
              <p>{{ faq.answer }}</p>
            </div>
          </div>
        </modal-base>
      </template>
    </div>
  </div>
</template>

<script>
import Icon from '@components/singletons/icon';
import LoadingBar from '@components/singletons/loading-bar';
import ModalBase from '@components/modals/ModalBase';
import PopBarChart from '@components/dashboard-widgets/PopBarChart';
import WidgetHeader from '@components/dashboard-widgets/WidgetHeader';

import DashboardWidgetMixin from '@/mixins/dashboardWidget';
import { useDefaultQuestionPath } from '@/composables/useDefaultQuestionPath';

export default {
  name: 'PerformancePossibilityOfPassingWidget',
  components: {
    Icon,
    LoadingBar,
    ModalBase,
    PopBarChart,
    WidgetHeader,
  },
  mixins: [DashboardWidgetMixin],
  props: {
    config: {
      type: Object,
      default: () => ({}),
    },
    dashboard: {
      type: Object,
      default: () => ({}),
    },
    identifier: {
      type: Number,
      default: null,
    },
    userHideable: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { defaultQuestionPath } = useDefaultQuestionPath();
    return { defaultQuestionPath };
  },
  data() {
    return {
      fetching: true,
      fetchError: false,
      showInfoModal: false,
    };
  },
  computed: {
    hasReadinessLevel() {
      return !!this.readinessState.popReadinessLevelId;
    },
    activeLevel() {
      if (!this.hasReadinessLevel) return null;
      return this.levels.find(l => l.id === this.readinessState.popReadinessLevelId) || null;
    },
    readinessState() {
      return this.$store.getters['popReadiness/readinessState'] || {};
    },
    levels() {
      return this.$store.getters['popReadiness/levels'];
    },
    faqs() {
      return this.$store.getters['popReadiness/faqs'];
    },
    progressPercent() {
      const progress = this.readinessState.nextUpdateProgress || 0;
      const total = this.readinessState.nextUpdateTotal || 1; // prevent division by zero
      return Math.min(Math.max((progress / total) * 100, 0), 100);
    },
    progressText() {
      const progress = this.readinessState.nextUpdateProgress || 0;
      const total = this.readinessState.nextUpdateTotal || 0;
      return `${progress}/${total}`;
    },
  },
  mounted() {
    this.$store.dispatch('popReadiness/fetchReadinessState')
      .then(() => {
        this.fetching = false;
      })
      .catch(() => {
        this.fetching = false;
        this.fetchError = true;
      });
  },
  methods: {
    openInfoModal() {
      this.showInfoModal = true;
      this.$analytics.events.popViewedInfo();
    },
    navigateToInsights() {
      this.$router.push({ name: 'popInsights#show' });
      this.$analytics.events.popViewedInsightsScreen();
    },
    navigateToQuestions() {
      this.$router.push(this.defaultQuestionPath);
    },
  },
};
</script>

<style lang="scss" scoped>
.performance-pop-widget {
  background: #FDFDFD;
  border: 1px solid var(--Colors-Border-border-secondary);
  border-radius: rem-calc(16);
  box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.performance-widget-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
}

/* Same rounded white shell as Questions Answered `.chart-wrapper` above the footer strip. */
.widget-content {
  background: var(--Colors-Foreground-fg-white);
  position: relative;
  padding: rem-calc(36);
  min-height: 200px;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: rem-calc(16);
  border-bottom: 1px solid var(--Colors-Border-border-secondary);
  overflow: hidden;
}

.chart-bg-only {
  opacity: 0.4;
}

.pre-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  backdrop-filter: blur(2px);
  border-radius: var(--radius-xl);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  gap: 12px;
}

.icon-circle {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--Colors-Background-bg-brand-secondary, #d1e0ff);
  border-radius: 9999px;

  .bar-chart-icon {
    width: 17px;
    height: 17px;
  }
}

.overlay-description {
  text-align: center;
  font-size: var(--Fontsizetext-sm);
  font-weight: var(--Fontweightfont-weight-medium);
  line-height: 1.43;
  margin: 0;
  max-width: rem-calc(300);
}

.progress-row {
  display: flex;
  align-items: center;
  gap: rem-calc(12);
  width: 100%;
  max-width: rem-calc(300);
}

.progress-track {
  flex: 1;
  height: 8px;
  background: var(--Colors-Background-bg-quaternary, #e9eaeb);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--fg-brand-primary-600);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  color: var(--text-tertiary);
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

.widget-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  min-height: rem-calc(60);
  padding: 12px 16px;
  flex-shrink: 0;
  width: 100%;
  background: #FDFDFD;
}

.info-button {
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 rem-calc(40);
  width: rem-calc(40);
  height: rem-calc(40);
  padding: 0;
  box-sizing: border-box;

  .help-circle {
    width: 20px;
    height: 20px;
  }
}

.answer-button {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid var(--Colors-Border-border-primary, #d5d7da);
  border-radius: 8px;
  padding: 0 14px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  min-height: rem-calc(40);
  max-height: rem-calc(40);
  height: rem-calc(40);
  color: var(--text-secondary);
  cursor: pointer;

  &:hover {
    background: var(--bg-secondary);
  }
}

.insights-button {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: var(--primary-bg);
  border: none;
  border-radius: 8px;
  padding: 0 14px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  min-height: rem-calc(40);
  max-height: rem-calc(40);
  height: rem-calc(40);
  color: white;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
}

.info-modal-content {
  .faq-item {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    h4 {
      margin: 0 0 4px;
      font-size: 14px;
      font-weight: 600;
    }

    p {
      margin: 0;
      font-size: 14px;
      line-height: 1.5;
    }
  }
}
</style>
